import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { savingsService } from '../../services/savingsService'

function SavingsDetail() {
  const { noRekening } = useParams()
  const navigate = useNavigate()
  const [account, setAccount] = useState(null)
  const [transactions, setTransactions] = useState([])
  const [loading, setLoading] = useState(true)
  const [showDepositModal, setShowDepositModal] = useState(false)
  const [showWithdrawModal, setShowWithdrawModal] = useState(false)
  const [transactionAmount, setTransactionAmount] = useState(0)
  const [transactionNote, setTransactionNote] = useState('')
  const [processing, setProcessing] = useState(false)

  useEffect(() => {
    fetchAccount()
  }, [noRekening])

  const fetchAccount = async () => {
    try {
      setLoading(true)
      const response = await savingsService.getById(noRekening)
      setAccount(response.data.data)
      setTransactions(response.data.data.transaksi || [])
    } catch (error) {
      console.error('Error fetching account:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDeposit = async () => {
    setProcessing(true)
    try {
      await savingsService.deposit(noRekening, transactionAmount, transactionNote)
      alert('Setoran berhasil')
      setShowDepositModal(false)
      setTransactionAmount(0)
      setTransactionNote('')
      fetchAccount()
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal melakukan setoran')
    } finally {
      setProcessing(false)
    }
  }

  const handleWithdraw = async () => {
    setProcessing(true)
    try {
      await savingsService.withdraw(noRekening, transactionAmount, transactionNote)
      alert('Penarikan berhasil')
      setShowWithdrawModal(false)
      setTransactionAmount(0)
      setTransactionNote('')
      fetchAccount()
    } catch (error) {
      alert(error.response?.data?.message || 'Gagal melakukan penarikan')
    } finally {
      setProcessing(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(value || 0)
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('id-ID')
  }

  if (loading) {
    return (
      <div className="text-center py-5">
        <i className="fas fa-spinner fa-spin fa-2x"></i>
        <p className="mt-2">Memuat data...</p>
      </div>
    )
  }

  if (!account) {
    return (
      <div className="alert alert-warning">
        Rekening tidak ditemukan
      </div>
    )
  }

  const isSukarela = account.KodeJenis === 'SUKARELA' || account.KodeJenis === 'BERJANGKA'

  return (
    <div>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Detail Rekening Simpanan</h3>
          <div className="card-tools">
            {account.Status === 'Aktif' && (
              <>
                
                {isSukarela && (
                  <button
                    className="btn btn-warning btn-sm mr-2"
                    onClick={() => setShowWithdrawModal(true)}
                  >
                    <i className="fas fa-minus mr-1"></i> Penarikan
                  </button>
                )}
              </>
            )}
            <button
              className="btn btn-default btn-sm"
              onClick={() => navigate('/members')}
            >
              <i className="fas fa-arrow-left mr-1"></i> Kembali
            </button>
          </div>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td width="150"><strong>No. Rekening</strong></td>
                    <td>: {account.NoRekening}</td>
                  </tr>
                  <tr>
                    <td><strong>NBA</strong></td>
                    <td>: {account.NBA}</td>
                  </tr>
                  <tr>
                    <td><strong>Nama Anggota</strong></td>
                    <td>: {account.NamaAnggota}</td>
                  </tr>
                  <tr>
                    <td><strong>Jenis Simpanan</strong></td>
                    <td>: {account.NamaJenis}</td>
                  </tr>
                  <tr>
                    <td><strong>Bunga</strong></td>
                    <td>: {account.BungaDasar}% / {account.PeriodeBunga}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="col-md-6">
              <div className="info-box bg-info">
                <span className="info-box-icon"><i className="fas fa-wallet"></i></span>
                <div className="info-box-content">
                  <span className="info-box-text">Saldo Saat Ini</span>
                  <span className="info-box-number" style={{ fontSize: '1.8rem' }}>
                    {formatCurrency(account.Saldo)}
                  </span>
                </div>
              </div>
              <div className="row">
                <div className="col-6">
                  <small>Bunga Bulan Ini: {formatCurrency(account.BungaBlnIni)}</small>
                </div>
                <div className="col-6 text-right">
                  <small>Bunga Tahun Ini: {formatCurrency(account.BungaThnIni)}</small>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <h5>Riwayat Transaksi</h5>
            <div className="table-responsive">
              <table className="table table-bordered table-sm">
                <thead className="thead-light">
                  <tr>
                    <th>Tanggal</th>
                    <th>Jenis</th>
                    <th>Debet</th>
                    <th>Kredit</th>
                    <th>Saldo Akhir</th>
                    <th>Keterangan</th>
                  </tr>
                </thead>
                <tbody>
                  {!Array.isArray(transactions) || transactions.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="text-center">Belum ada transaksi</td>
                    </tr>
                  ) : (
                    transactions.map((trx) => (
                      <tr key={trx.Id}>
                        <td>{formatDate(trx.TglTransaksi)}</td>
                        <td>
                          <span className={`badge ${
                            trx.JenisTransaksi === 'Setoran' ? 'badge-success' :
                            trx.JenisTransaksi === 'Penarikan' ? 'badge-danger' :
                            'badge-info'
                          }`}>
                            {trx.JenisTransaksi}
                          </span>
                        </td>
                        <td className="text-right text-danger">
                          {trx.Debet > 0 ? formatCurrency(trx.Debet) : '-'}
                        </td>
                        <td className="text-right text-success">
                          {trx.Kredit > 0 ? formatCurrency(trx.Kredit) : '-'}
                        </td>
                        <td className="text-right">{formatCurrency(trx.SaldoAkhir)}</td>
                        <td>{trx.Keterangan || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {showDepositModal && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header bg-success">
                <h5 className="modal-title">Setoran</h5>
                <button type="button" className="close text-white" onClick={() => setShowDepositModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Jumlah Setoran *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={transactionAmount}
                    onChange={(e) => setTransactionAmount(parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>
                <div className="form-group">
                  <label>Keterangan</label>
                  <input
                    type="text"
                    className="form-control"
                    value={transactionNote}
                    onChange={(e) => setTransactionNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDepositModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleDeposit}
                  disabled={processing || transactionAmount <= 0}
                >
                  {processing ? 'Memproses...' : 'Simpan'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showWithdrawModal && (
        <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-sm">
            <div className="modal-content">
              <div className="modal-header bg-warning">
                <h5 className="modal-title">Penarikan</h5>
                <button type="button" className="close" onClick={() => setShowWithdrawModal(false)}>
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Jumlah Penarikan *</label>
                  <input
                    type="number"
                    className="form-control"
                    value={transactionAmount}
                    onChange={(e) => setTransactionAmount(parseInt(e.target.value) || 0)}
                    min="1"
                  />
                  <small className="form-text text-muted">
                    Saldo tersedia: {formatCurrency(account.Saldo)}
                  </small>
                </div>
                <div className="form-group">
                  <label>Keterangan</label>
                  <input
                    type="text"
                    className="form-control"
                    value={transactionNote}
                    onChange={(e) => setTransactionNote(e.target.value)}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowWithdrawModal(false)}>
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-warning"
                  onClick={handleWithdraw}
                  disabled={processing || transactionAmount <= 0 || transactionAmount > account.Saldo}
                >
                  {processing ? 'Memproses...' : 'Tarik'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SavingsDetail
